import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormikContext, getIn } from 'formik';
import { ArtikelZulage, ArtikelZulageFormData } from '../../../shared/types/artikelZulage';
import { getArtikelZulagen, ArtikelZulageSelectors } from '../../../store/ArtikelZulage.store';
import { createArtikelZulageFilter, useArtikelZulagePartnerContext } from '../../hooks/useArtikelZulagePartnerContext';
import Select from 'react-select';
import { createCustomStyles } from '../../ui-components/SelectInput/CustomStyles';
import TextInputWithoutFormik from '../../ui-components/TextInput/TextInputWithoutFormik';
import Label from '../../ui-components/Label/Label';
import { useMediaQuery } from 'react-responsive';
import styles from './ArtikelZulageMultipleAuswahl.module.scss';
import zulageListeStyles from './ZulageListeEntryText.module.scss';
import TrashIcon from '../../../assets/icon-trashcan.svg';
import TrashIconZulage from '../../../assets/icon-trashcan-zulage.svg';
import AddIcon from '../../../assets/icon-add.svg';
import InfoIcon from '../../../assets/icon-info.svg';
import FlaskIcon from '../../../assets/icon-artikelzulage-flask.svg';
import DosierungIcon from '../../../assets/icon-dosierung.svg';

// Lokale Interface-Definitionen
interface ArtikelZulageEntry {
    zulageId?: string;
    freitext?: string;
    dosierwert?: number;
}

interface ArtikelZulageMultipleFormData {
    zulagen: ArtikelZulageEntry[];
}

interface ArtikelZulageMultipleAuswahlProps {
    artikelgruppeId: string;
    werkId?: string;
    name: string;
    label?: string;
    placeholder?: string;
    isRequired?: boolean;
    disabled?: boolean;
}

interface ZulageOption {
    value: string;
    label: string;
    zulage: ArtikelZulage;
}

const ArtikelZulageMultipleAuswahl: React.FC<ArtikelZulageMultipleAuswahlProps> = ({
    artikelgruppeId,
    werkId,
    name,
    label = 'Artikelzulagen',
    placeholder = 'Zulage auswählen oder eingeben',
    isRequired = false,
    disabled = false,
}) => {
    const dispatch = useDispatch();
    const { setFieldValue, values } = useFormikContext<ArtikelZulageMultipleFormData>();
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
    const [openDropdownIndices, setOpenDropdownIndices] = useState<Set<number>>(new Set());
    const [zulageAktiv, setZulageAktiv] = useState<boolean>(false);
    const [hasRetriedWithoutWerk, setHasRetriedWithoutWerk] = useState(false);

    // Redux State – filter by artikelgruppe + werk + Warenempfänger (artikel_zulage_partner)
    const { warenempfaengerPartnerNummer, rechnungsempfaengerPartnerNummer } = useArtikelZulagePartnerContext();
    const currentFilter = {
        artikelgruppeId,
        werkId,
        partnerNummer: warenempfaengerPartnerNummer,
        rechnungsempfaengerPartnerNummer: rechnungsempfaengerPartnerNummer || undefined,
    };
    const zulagen = useSelector((state: any) => ArtikelZulageSelectors.aktiveZulagenByFilter(state, currentFilter));
    const isLoading = useSelector(ArtikelZulageSelectors.loading);
    const loadFailed = useSelector(ArtikelZulageSelectors.loadFailed);
    const loadFinished = useSelector(ArtikelZulageSelectors.loadFinished);

    // Get current zulagen array from formik (unterstützt verschachtelte Pfade z. B. bestellpositionenView[0].zulagenData)
    const formPath = name.replace(/\[(\d+)\]/g, '.$1');
    const currentZulagen = getIn(values, formPath)?.zulagen || [];
    const basePath = name.replace(/\.zulagenData$/, '');

    const syncParentZulage = (arr: ArtikelZulageEntry[]) => {
        setFieldValue(`${basePath}.zulage`, arr.length > 0);
    };

    // Lade Zulagen beim Mount und bei Änderungen
    useEffect(() => {
        const filter = createArtikelZulageFilter(artikelgruppeId, werkId, {
            warenempfaengerPartnerNummer,
            rechnungsempfaengerPartnerNummer,
        });
        if (filter) {
            dispatch(getArtikelZulagen(filter));
        }
    }, [dispatch, artikelgruppeId, werkId, warenempfaengerPartnerNummer, rechnungsempfaengerPartnerNummer]);

    // Beim Wechsel des Filters Fallback-Status zurücksetzen.
    useEffect(() => {
        setHasRetriedWithoutWerk(false);
    }, [artikelgruppeId, werkId, warenempfaengerPartnerNummer, rechnungsempfaengerPartnerNummer]);

    // Bestelldetails-Edit: wenn werk-spezifisch leer, einmal ohne werkId laden.
    useEffect(() => {
        if (!artikelgruppeId || !warenempfaengerPartnerNummer || !werkId) return;
        if (isLoading || !loadFinished || loadFailed) return;
        if (hasRetriedWithoutWerk || zulagen.length > 0) return;

        setHasRetriedWithoutWerk(true);
        const filter = createArtikelZulageFilter(artikelgruppeId, undefined, {
            warenempfaengerPartnerNummer,
            rechnungsempfaengerPartnerNummer,
        });
        if (filter) {
            dispatch(getArtikelZulagen(filter));
        }
    }, [
        dispatch,
        artikelgruppeId,
        werkId,
        warenempfaengerPartnerNummer,
        rechnungsempfaengerPartnerNummer,
        isLoading,
        loadFinished,
        loadFailed,
        hasRetriedWithoutWerk,
        zulagen.length,
    ]);

    // Mobile: Bei aktiviertem Häkchen erste Zulage anlegen; bei deaktiviertem Liste leeren
    useEffect(() => {
        if (!isMobile) return;
        if (zulageAktiv && currentZulagen.length === 0) {
            handleAddZulage();
        }
        if (!zulageAktiv && currentZulagen.length > 0) {
            setFieldValue(`${name}.zulagen`, []);
            syncParentZulage([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zulageAktiv, isMobile]);

    // Desktop (Bestellung anlegen): Sobald „Zulage?“ aktiv ist und die Liste leer ist, direkt erste Zeile (Menü) anzeigen statt „Artikelzulage hinzufügen“
    useEffect(() => {
        if (isMobile) return;
        if (currentZulagen.length === 0) {
            handleAddZulage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, currentZulagen.length]);

    const hasDropdownOptions = zulagen.length > 0;
    const hasStructuredZulageRow = currentZulagen.some((z: ArtikelZulageEntry) => z.zulageId);
    const canLoadArtikelZulagen = Boolean(artikelgruppeId && warenempfaengerPartnerNummer);
    // Wie ZulageMobileDropdown: solange Artikelzulagen-Dropdown möglich/geladen werden, keine Sonderherstellung-Freitext-Pflicht
    const showDropdownLayout =
        hasDropdownOptions || hasStructuredZulageRow || (canLoadArtikelZulagen && isLoading) || (canLoadArtikelZulagen && loadFailed);

    useEffect(() => {
        setFieldValue(`${basePath}.zulageHatArtikelDropdown`, showDropdownLayout);
    }, [basePath, setFieldValue, showDropdownLayout]);

    // Dropdown-Optionen erstellen (Preis mit Komma wie in Figma)
    const formatPreis = (preis: number | string | undefined): string => {
        if (preis === undefined || preis === null) return '';
        const num = typeof preis === 'number' ? preis : parseFloat(String(preis));
        return Number.isNaN(num) ? String(preis) : num.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
    };

    const zulageOptions: ZulageOption[] = zulagen.map((zulage) => {
        return {
            value: zulage.id,
            label:
                zulage.preis !== undefined && zulage.einheit
                    ? `${zulage.bezeichnung} (+${formatPreis(zulage.preis)} ${zulage.einheit})`
                    : zulage.bezeichnung,
            zulage,
        };
    });

    // zulageText für Validierung immer aus zulagenData ableiten, damit „Bitte definieren Sie die Zulage …“ verschwindet, sobald etwas eingetragen ist
    useEffect(() => {
        if (isMobile) return;
        if (!hasDropdownOptions && currentZulagen.length === 1) {
            const freitext = currentZulagen[0]?.freitext?.trim() ?? '';
            const currentZulageText = getIn(values, `${basePath}.zulageText`) ?? '';
            if (freitext !== currentZulageText) {
                setFieldValue(`${basePath}.zulageText`, freitext);
            }
        } else if (hasDropdownOptions && currentZulagen.length > 0) {
            const firstWithId = currentZulagen.find((z: ArtikelZulageEntry) => z.zulageId);
            const firstWithFreitext = currentZulagen.find((z: ArtikelZulageEntry) => z.freitext?.trim());
            const labelFromId = firstWithId ? zulageOptions.find((o) => o.value === firstWithId.zulageId)?.label : undefined;
            const textToSet = labelFromId ?? firstWithFreitext?.freitext?.trim() ?? '';
            const currentZulageText = getIn(values, `${basePath}.zulageText`) ?? '';
            if (textToSet !== currentZulageText) {
                setFieldValue(`${basePath}.zulageText`, textToSet);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, hasDropdownOptions, currentZulagen, basePath, name, values, zulageOptions]);

    const showFreitextHint = !hasDropdownOptions;

    const MAX_ZULAGEN = 4;

    // Handler für neue Zulage hinzufügen
    const handleAddZulage = () => {
        const current = getIn(values, formPath);
        const currentLen = current?.zulagen?.length ?? 0;
        if (currentLen >= MAX_ZULAGEN) return;
        if (!current?.zulagen || currentLen === 0) {
            const newZulagen = [{ zulageId: undefined, freitext: '', dosierwert: undefined }];
            setFieldValue(name, { zulagen: newZulagen });
            syncParentZulage(newZulagen);
            setOpenDropdownIndices(new Set([0]));
            return;
        }
        const currentZulagenArr: ArtikelZulageEntry[] = current.zulagen;
        const newZulagen = [...currentZulagenArr, { zulageId: undefined, freitext: '', dosierwert: undefined }];
        setFieldValue(`${name}.zulagen`, newZulagen);
        syncParentZulage(newZulagen);

        const newIndices = new Set(openDropdownIndices);
        newIndices.add(newZulagen.length - 1);
        setOpenDropdownIndices(newIndices);
    };

    // Handler für Zulage entfernen
    const handleRemoveZulage = (index: number) => {
        const newZulagen = currentZulagen.filter((_: ArtikelZulageEntry, i: number) => i !== index);
        setFieldValue(`${name}.zulagen`, newZulagen);
        syncParentZulage(newZulagen);
        // zulageText für Validierung aktualisieren: erste verbleibende Auswahl oder Freitext, sonst leer
        const firstWithValue =
            newZulagen.length > 0 && newZulagen[0].zulageId
                ? zulageOptions.find((o) => o.value === newZulagen[0].zulageId)?.label
                : newZulagen[0]?.freitext?.trim() ?? '';
        setFieldValue(`${basePath}.zulageText`, firstWithValue);

        const newIndices = new Set<number>();
        openDropdownIndices.forEach((idx) => {
            if (idx < index) newIndices.add(idx);
            else if (idx > index) newIndices.add(idx - 1);
        });
        setOpenDropdownIndices(newIndices);
    };

    // Handler für Dropdown-Auswahl
    const handleZulageSelect = (option: any, index: number) => {
        const zulageOption = option as ZulageOption | null;
        const newZulagen = [...currentZulagen];
        const defaultDosierwert = zulageOption?.zulage?.dosierbereichMin ?? 0;

        if (zulageOption) {
            newZulagen[index] = {
                ...newZulagen[index],
                zulageId: zulageOption.value,
                freitext: undefined,
                dosierwert: defaultDosierwert,
            };
        } else {
            newZulagen[index] = {
                ...newZulagen[index],
                zulageId: undefined,
                dosierwert: undefined,
            };
        }

        setFieldValue(`${name}.zulagen`, newZulagen);
        // Validierung prüft zulageText – bei Dropdown-Auswahl ebenfalls setzen, damit die Meldung verschwindet
        if (zulageOption) {
            setFieldValue(`${basePath}.zulageText`, zulageOption.label);
        } else {
            const firstWithText = newZulagen.find((z) => z.freitext?.trim());
            setFieldValue(`${basePath}.zulageText`, firstWithText?.freitext ?? '');
        }
    };

    // Handler für Freitext-Änderung
    const handleFreitextChange = (value: string, index: number) => {
        const newZulagen = [...currentZulagen];
        newZulagen[index] = {
            ...newZulagen[index],
            freitext: value,
            zulageId: undefined,
        };
        setFieldValue(`${name}.zulagen`, newZulagen);
        // Validierung prüft zulageText – bei nur einem Freitext-Eintrag (ohne Dropdown) synchron halten
        const isSingleFreitext = newZulagen.length === 1 && !newZulagen[0].zulageId;
        if (isSingleFreitext) {
            setFieldValue(`${basePath}.zulageText`, newZulagen[0].freitext ?? '');
        }
    };

    // Handler für Dosierwert-Änderung
    const handleDosierwertChange = (value: string, index: number) => {
        const numValue = value ? parseFloat(value) : undefined;
        const newZulagen = [...currentZulagen];
        newZulagen[index] = {
            ...newZulagen[index],
            dosierwert: numValue,
        };
        setFieldValue(`${name}.zulagen`, newZulagen);
    };

    // Dosierung Step (0.1) für +/- Buttons
    const handleDosierwertStep = (index: number, delta: number) => {
        const entry = currentZulagen[index];
        const selectedOption = getSelectedZulage(index);
        const currentZulage = selectedOption?.zulage;
        const min = currentZulage?.dosierbereichMin ?? 0;
        const max = currentZulage?.dosierbereichMax ?? 99;
        const currentVal = entry?.dosierwert ?? min;
        const newVal = Math.max(min, Math.min(max, Math.round((currentVal + delta) * 10) / 10));
        handleDosierwertChange(String(newVal), index);
    };

    // Get selected zulage for display (Option aus der API-Liste)
    const getSelectedZulage = (index: number) => {
        const zulageEntry = currentZulagen[index];
        if (!zulageEntry?.zulageId) return null;
        return zulageOptions.find((z) => z.value === zulageEntry.zulageId) || null;
    };

    // Anzeige-Wert für das Select: gespeicherte Zulage (z. B. aus Bestellansicht) auch anzeigen, wenn sie nicht in zulageOptions ist (z. B. code vs. id)
    const getSelectDisplayValue = (index: number): ZulageOption | { value: string; label: string } | null => {
        const selected = getSelectedZulage(index);
        if (selected) return selected;
        const zulageEntry = currentZulagen[index];
        if (!zulageEntry) return null;
        const label = zulageEntry.freitext?.trim() || zulageEntry.zulageId || '';
        if (!label && !zulageEntry.zulageId) return null;
        return { value: zulageEntry.zulageId ?? '', label };
    };

    // Echte Artikelzulagen (Dropdown): mind. ein Eintrag mit zulageId UND (mehrere Einträge ODER Dosierung gesetzt).
    // So wird reine Sonderherstellung (ein Eintrag, nur Freitext / zulageId ohne Dosierung) nicht als Dropdown gezählt.
    const hasSavedDropdownZulagen =
        currentZulagen.length > 1 && currentZulagen.some((z: ArtikelZulageEntry) => !!z.zulageId)
            ? true
            : currentZulagen.some((z: ArtikelZulageEntry) => !!z.zulageId && z.dosierwert != null);

    // Desktop/Web: Textfeld nur wenn Artikel keine Dropdown-Optionen hat UND keine gespeicherten Artikelzulagen (mit Dosierung/mehrere).
    if (!isMobile) {
        // Nur Freitext-Zulage (Sonderherstellung): kein Dropdown vom Artikel, keine gespeicherten Dropdown-Zulagen → Textfeld "Zulage (*)"
        if (!showDropdownLayout && !hasDropdownOptions && !hasSavedDropdownZulagen) {
            const singleEntry = currentZulagen[0] ?? { zulageId: undefined, freitext: '', dosierwert: undefined };
            return (
                <div className={styles.artikelZulageMultipleAuswahl}>
                    <div className={zulageListeStyles.container}>
                        <label className={zulageListeStyles.label} htmlFor={`${name}.zulagen[0].freitext`}>
                            Zulage (*)
                        </label>
                        <div className={zulageListeStyles.inputWrapper}>
                            <TextInputWithoutFormik
                                name={`${name}.zulagen[0].freitext`}
                                id={`${name}.zulagen[0].freitext`}
                                value={singleEntry.freitext || ''}
                                onFieldChange={(value) => handleFreitextChange(value, 0)}
                                disabled={disabled}
                            />
                        </div>
                    </div>
                    {loadFailed && <div className={styles.error}>Fehler beim Laden der Zulagen. Bitte versuchen Sie es noch einmal.</div>}
                </div>
            );
        }

        // Artikel hat Zulage-Optionen ODER gespeicherte Artikelzulagen (Bestellansicht) → Dropdown-UI, Dosierung, „+ hinzufügen“, Trash
        return (
            <div className={styles.artikelZulageMultipleAuswahl}>
                {currentZulagen.map((zulageEntry: ArtikelZulageEntry, index: number) => {
                    const selectedZulage = getSelectedZulage(index);
                    const currentZulage = selectedZulage?.zulage;
                    const dosierwertDisplay =
                        zulageEntry.dosierwert !== undefined && zulageEntry.dosierwert !== null
                            ? zulageEntry.dosierwert
                            : currentZulage?.dosierbereichMin ?? 0;
                    const dosierwertFormatted =
                        typeof dosierwertDisplay === 'number'
                            ? dosierwertDisplay.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 2 })
                            : String(dosierwertDisplay).replace('.', ',');

                    return (
                        <div key={index} className={styles.zulageEntry}>
                            <div className={styles.zulageDetailsRow}>
                                <div className={styles.frame181}>
                                    <label className={styles.zulageFieldLabel}>Artikelzulage</label>
                                    <div className={styles.artikelZulageNameFrame}>
                                        <div className={styles.artikelZulageSelectWrap}>
                                            <Select
                                                classNamePrefix="react_select__form-zulage"
                                                options={zulageOptions}
                                                value={getSelectDisplayValue(index)}
                                                onChange={(option) => handleZulageSelect(option, index)}
                                                placeholder={placeholder}
                                                isLoading={isLoading}
                                                isDisabled={disabled}
                                                styles={createCustomStyles(false, false, 'left', false)}
                                                menuShouldScrollIntoView={true}
                                                menuPlacement="auto"
                                                isClearable={true}
                                                noOptionsMessage={() => 'Keine Zulagen verfügbar'}
                                            />
                                        </div>
                                        <img src={FlaskIcon} alt="" className={styles.artikelZulageNameIcon} aria-hidden />
                                    </div>
                                </div>
                                <span className={styles.infoIconBetween} aria-hidden title="Info">
                                    <img src={InfoIcon} alt="" width={20} height={20} />
                                </span>
                                <div className={styles.dosierungInfoBoxWrapper}>
                                    <label className={styles.dosierungLabel}>Dosierung</label>
                                    <div className={styles.dosierungInfoBox}>
                                        <span className={styles.dosierungIconInField} aria-hidden title="Dosierung">
                                            <img src={DosierungIcon} alt="" className={styles.dosierungIconLeft} />
                                        </span>
                                        <span className={styles.dosierungValueWrap}>
                                            <span className={styles.dosierungDisplay}>{dosierwertFormatted}</span>
                                            <span className={styles.dosierungEinheit}>x</span>
                                        </span>
                                        <span className={styles.dosierungIconGroup}>
                                            <button
                                                type="button"
                                                className={styles.dosierungStepButton}
                                                onClick={() => handleDosierwertStep(index, -0.1)}
                                                disabled={disabled}
                                                aria-label="Dosierung verringern"
                                            >
                                                −
                                            </button>
                                            <span className={styles.dosierungStepSep} aria-hidden />
                                            <button
                                                type="button"
                                                className={styles.dosierungStepButton}
                                                onClick={() => handleDosierwertStep(index, 0.1)}
                                                disabled={disabled}
                                                aria-label="Dosierung erhöhen"
                                            >
                                                +
                                            </button>
                                        </span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className={styles.deleteButton}
                                    onClick={() => handleRemoveZulage(index)}
                                    disabled={disabled}
                                    aria-label="Zulage entfernen"
                                >
                                    <img src={TrashIconZulage} alt="" width={22} height={44} />
                                </button>
                            </div>
                        </div>
                    );
                })}

                <div className={styles.addZulageWrapper}>
                    <button
                        type="button"
                        className={styles.addZulageLink}
                        onClick={handleAddZulage}
                        disabled={disabled || currentZulagen.length >= MAX_ZULAGEN}
                    >
                        <span className={styles.addZulagePlus}>+</span> Artikelzulage hinzufügen
                    </button>
                </div>

                {loadFailed && <div className={styles.error}>Fehler beim Laden der Zulagen. Bitte versuchen Sie es noch einmal.</div>}
            </div>
        );
    }

    // Mobile: Checkbox + Beide Optionen (Textfeld UND Dropdown)
    return (
        <div className={styles.artikelZulageMultipleAuswahl}>
            <div className={styles.header}>
                <Label htmlFor={name}>{label}</Label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
                    <input
                        type="checkbox"
                        checked={zulageAktiv}
                        onChange={(e) => setZulageAktiv(e.target.checked)}
                        style={{ width: 18, height: 18 }}
                        disabled={disabled}
                    />
                    Zulage?
                </label>
            </div>

            {zulageAktiv && (
                <>
                    {/* Zuerst erste Zeile (Menü) anzeigen, darunter „+ Artikelzulage hinzufügen“ */}
                    {currentZulagen.map((zulageEntry: ArtikelZulageEntry, index: number) => {
                        const selectedZulage = getSelectedZulage(index);
                        const currentZulage = selectedZulage?.zulage;
                        const hasDropdownOptionsForEntry = zulageOptions.length > 0;
                        return (
                            <div key={index} className={styles.zulageEntry}>
                                <div className={styles.zulageHeader}>
                                    <span className={styles.zulageNumber}>Artikelzulage</span>
                                    <button
                                        type="button"
                                        className={styles.iconButton}
                                        onClick={() => handleRemoveZulage(index)}
                                        disabled={disabled}
                                        aria-label="Zulage entfernen"
                                    >
                                        <img src={TrashIcon} alt="Entfernen" width={20} height={20} />
                                    </button>
                                </div>
                                <div className={styles.zulageDetailsRow}>
                                    <div className={styles.zulageFieldWrapper}>
                                        {/* Mobile: Beide Optionen - Dropdown UND Textfeld */}
                                        {hasDropdownOptionsForEntry && (
                                            <div className={styles.dropdownSection}>
                                                <Label>Aus bestehenden Zulagen wählen:</Label>
                                                <Select
                                                    options={zulageOptions}
                                                    value={getSelectDisplayValue(index)}
                                                    onChange={(option) => handleZulageSelect(option, index)}
                                                    placeholder="Zulage auswählen"
                                                    isLoading={isLoading}
                                                    isDisabled={disabled}
                                                    styles={createCustomStyles(false, false, 'left', false, true)}
                                                    menuShouldScrollIntoView={true}
                                                    menuPlacement="auto"
                                                    isClearable={true}
                                                    noOptionsMessage={() => 'Keine Zulagen verfügbar'}
                                                    menuIsOpen={openDropdownIndices.has(index)}
                                                    onMenuOpen={() => {
                                                        const newIndices = new Set(openDropdownIndices);
                                                        newIndices.add(index);
                                                        setOpenDropdownIndices(newIndices);
                                                    }}
                                                    onMenuClose={() => {
                                                        const newIndices = new Set(openDropdownIndices);
                                                        newIndices.delete(index);
                                                        setOpenDropdownIndices(newIndices);
                                                    }}
                                                />
                                            </div>
                                        )}

                                        <div className={styles.freitextSection}>
                                            <Label>Oder neue Zulage eingeben:</Label>
                                            <TextInputWithoutFormik
                                                name={`${name}.zulagen[${index}].freitext`}
                                                id={`${name}.zulagen[${index}].freitext`}
                                                value={zulageEntry.freitext || ''}
                                                onFieldChange={(value) => handleFreitextChange(value, index)}
                                                disabled={disabled}
                                            />
                                        </div>
                                    </div>
                                    {selectedZulage && (
                                        <div className={styles.dosierungInfoBoxWrapper}>
                                            <label className={styles.dosierungLabel}>Dosierung</label>
                                            <div className={styles.dosierungInfoBox}>
                                                <span className={styles.dosierungIconInField} aria-hidden title="Dosierung">
                                                    <img src={DosierungIcon} alt="" width={24} height={24} />
                                                </span>
                                                <span>
                                                    {currentZulage?.dosierbereichMin !== undefined ||
                                                    currentZulage?.dosierbereichMax !== undefined
                                                        ? (() => {
                                                              const opts = {
                                                                  minimumFractionDigits: 1,
                                                                  maximumFractionDigits: 2,
                                                              };
                                                              const minStr = (currentZulage?.dosierbereichMin ?? 0).toLocaleString(
                                                                  'de-DE',
                                                                  opts
                                                              );
                                                              const maxVal = currentZulage?.dosierbereichMax;
                                                              const maxStr =
                                                                  maxVal === undefined || maxVal === null
                                                                      ? '∞'
                                                                      : (maxVal as number).toLocaleString('de-DE', opts);
                                                              return `${minStr} - ${maxStr}`;
                                                          })()
                                                        : '—'}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    <div className={styles.addZulageWrapper}>
                        <button
                            type="button"
                            className={styles.addZulageButton}
                            onClick={handleAddZulage}
                            disabled={disabled || currentZulagen.length >= MAX_ZULAGEN}
                        >
                            <img src={AddIcon} alt="Hinzufügen" width={20} height={20} style={{ marginRight: 8 }} />
                            Artikelzulage hinzufügen
                        </button>
                    </div>
                </>
            )}

            {/* Fehler-Anzeige */}
            {loadFailed && <div className={styles.error}>Fehler beim Laden der Zulagen. Bitte versuchen Sie es noch einmal.</div>}
        </div>
    );
};

export default ArtikelZulageMultipleAuswahl;
